import { datadogLogs } from '@datadog/browser-logs';
import {
  addDays,
  differenceInDays,
  format,
  isValid,
  parse,
  parseISO,
  startOfDay,
} from 'date-fns';
import { formatInTimeZone, getTimezoneOffset } from 'date-fns-tz';

export const readableDateFormat = "MMM d, yyyy 'at' h:mma zzz";
export const fullDateOn = "MM/d/yyyy 'at' hh:mm aaa zzz";
export const fullDateOnWithoutTZ = "MM/d/yyyy 'at' hh:mm aaa";
export const simpleDateFormat = 'MM/dd/yyyy';
export const singleDigitDateFormat = 'M/d/yyyy';

/**
 * First matching weekday on or after `ymd` (YYYY-MM-DD).
 * Healthie snaps range_start to day_of_week within the week (including backward);
 * callers align forward so availability never opens before the intended start.
 */
export function onOrAfterDayOfWeek(ymd: string, dayOfWeek: number): string {
  let cursor = parseISO(ymd);
  if (!isValid(cursor)) {
    throw new RangeError(`Invalid YYYY-MM-DD date: ${ymd}`);
  }
  if (!Number.isInteger(dayOfWeek) || dayOfWeek < 0 || dayOfWeek > 6) {
    throw new RangeError(`Invalid day of week: ${dayOfWeek}`);
  }
  while (cursor.getDay() !== dayOfWeek) {
    cursor = addDays(cursor, 1);
  }
  return format(cursor, 'yyyy-MM-dd');
}

/** True when aligning startOn forward to dayOfWeek lands after endOn. */
export function startAlignsPastEnd(
  startOn: string,
  endOn: string,
  dayOfWeek: number,
): boolean {
  return onOrAfterDayOfWeek(startOn, dayOfWeek) > endOn;
}

/**
 * Formats a Healthie date string to a human-readable display format (e.g. "Jan 5, 2025").
 *
 * Returns `null` and logs to Datadog if the date is invalid or missing.
 * Callers that need a non-null fallback can use `?? dateString`.
 */
export function formatHealthieDisplayDate(
  dateString: string | null | undefined,
): string | null {
  if (!dateString) return null;

  try {
    return format(sanitizeHealthieDate(dateString), 'MMM d, yyyy');
  } catch (err) {
    datadogLogs.logger.warn('formatHealthieDisplayDate: failed to parse date', {
      err,
      dateString,
    });
    return null;
  }
}

/**
 * Sanitizes Healthie date format to ensure cross-browser compatibility.
 * Fixes issue with Safari parsing Healthie dates incorrectly.
 *
 * @param date Date value that might be in a Safari-incompatible format, or null/undefined
 * @returns A properly formatted Date object. Returns current date if input is invalid or missing.
 */
export const sanitizeHealthieDate = (date: string | undefined | null): Date => {
  if (!date || typeof date !== 'string') {
    if (date !== undefined && date !== null) {
      datadogLogs.logger.warn(
        'sanitizeHealthieDate received non-string input',
        {
          inputType: typeof date,
        },
      );
    }
    return new Date();
  }

  // Try parsing using the 'yyyy-MM-dd' format
  const parsedDate = parse(date, 'yyyy-MM-dd', new Date());
  if (isValid(parsedDate)) return parsedDate;

  // Replace spaces with 'T' to normalize to ISO-like format and try parsing
  const formattedDateString = date.replace(' ', 'T').replace(' ', '');
  const formattedDate = new Date(formattedDateString);
  if (!isNaN(formattedDate.getTime())) return formattedDate;

  // Try creating a Date object directly from the input string
  const directDate = new Date(date);
  if (!isNaN(directDate.getTime())) return directDate;

  // Fallback: return current date if all parsing attempts failed
  return new Date();
};

/**
 * Converts a Healthie datetime string (see `docs/healthie.md`) to ISO 8601 UTC.
 * Returns null when the input is missing or empty.
 */
export const healthieDatetimeToIso8601 = (
  healthieDatetime: string | null | undefined,
): string | null => {
  if (healthieDatetime == null || healthieDatetime === '') {
    return null;
  }
  return sanitizeHealthieDate(healthieDatetime).toISOString();
};

/** HH:MM from a Healthie datetime stamp (`YYYY-MM-DD HH:MM:SS ±ZZZZ`). */
export const healthieTimeOfDay = (
  stamp: string | null | undefined,
): string | null => {
  if (!stamp || stamp.length < 16) return null;
  return stamp.slice(11, 16);
};

/**
 * Wall-clock HH:MM of a Healthie stamp in `timezone`.
 * Healthie often serializes availability stamps with an Eastern offset even when
 * `timezone` is Pacific — use this instead of `healthieTimeOfDay` for rules.
 * Falls back to literal stamp digits when timezone is missing.
 *
 * This helper only converts the time. Native and unprojectable rows use the
 * row's timezone; projected rows derive hours, weekday, and anchor together
 * from the stamp in `projectAvailabilityRow`. It reads a window's closing
 * midnight as 00:00; use `healthieEndTimeInTimezone` for a window's end.
 */
export const healthieTimeOfDayInTimezone = (
  stamp: string | null | undefined,
  timezone: string | null | undefined,
): string | null => {
  if (!stamp || stamp.length < 16) return null;
  if (!timezone) return healthieTimeOfDay(stamp);
  return formatInTimeZone(sanitizeHealthieDate(stamp), timezone, 'HH:mm');
};

/**
 * Civil date of a Healthie stamp in `timezone`. The stamp's `YYYY-MM-DD`
 * prefix is Eastern; a west-coast evening one-off is the next morning there.
 */
export const healthieDateInTimezone = (
  stamp: string | null | undefined,
  timezone: string | null | undefined,
): string | null => {
  if (!stamp || stamp.length < 10) return null;
  if (!timezone) {
    const ymd = stamp.slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? ymd : null;
  }
  return formatInTimeZone(sanitizeHealthieDate(stamp), timezone, 'yyyy-MM-dd');
};

/**
 * Winter + summer probes. One probe would merge zones that only agree outside
 * DST (America/Phoenix vs America/Los_Angeles in July).
 */
const TIMEZONE_PROBES = [
  new Date(Date.UTC(2026, 0, 15)),
  new Date(Date.UTC(2026, 6, 15)),
];

/**
 * Whether two IANA identifiers describe the same wall clock, compared by real
 * UTC offset rather than by string. tzdb backward-compat links must match their
 * primary name: Healthie stores `US/Pacific` on older availability rules while
 * the same provider's Healthie profile reads `America/Los_Angeles`.
 * An empty identifier needs the guard below — `getTimezoneOffset` reads it as
 * UTC rather than rejecting it.
 *
 * ponytail: offset equality, not identifier identity. Distinct zones that always
 * share an offset (America/New_York vs America/Toronto) read as the same, which
 * is what callers want — they only ask to decide whether a rule's wall clock is
 * trustworthy. Ceiling: zones whose DST transitions fall at different instants
 * (America/Havana vs America/New_York) also read as the same, and genuinely
 * differ for a few hours a year. Upgrade path: compare canonical tzdb ids once
 * `Intl` canonicalisation of links is consistent across our target browsers.
 */
export const isSameTimezone = (
  a: string | null | undefined,
  b: string | null | undefined,
): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  // An unknown identifier offsets to NaN, which never compares equal — bad
  // input falls out as "different" without a separate guard.
  return TIMEZONE_PROBES.every(
    (probe) => getTimezoneOffset(a, probe) === getTimezoneOffset(b, probe),
  );
};

/**
 * Returns the provided timezone or falls back to the browser's timezone
 */
export const resolveTimezone = (timezone?: string | null): string => {
  return timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * The provider's Healthie timezone when it is a usable IANA id.
 *
 * Scheduling must fail closed when Healthie has no usable timezone. The
 * browser timezone is a display-only preference and must never become the
 * provider timezone implicitly.
 */
export const resolveProviderTimezone = (
  timezone: string | null | undefined,
): string | null => {
  if (!timezone) return null;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone });
    return timezone;
  } catch {
    return null;
  }
};

/**
 * Formats an ISO datetime in a timezone using date-fns format tokens.
 * Returns null when the input is missing, blank, or unparseable.
 */
export function formatIsoDateInTimezone(
  isoDate: string | null | undefined,
  formatStr: string,
  timezone: string = resolveTimezone(),
): string | null {
  if (!isoDate?.trim()) {
    return null;
  }

  const parsed = parseISO(isoDate);
  if (!isValid(parsed)) {
    return null;
  }

  return formatInTimeZone(parsed, timezone, formatStr);
}

/** The calendar day it is now in `timezone`, `YYYY-MM-DD`. */
export function todayInTimezone(timezone: string): string {
  return formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');
}

/**
 * Returns a DST-agnostic timezone abbreviation (e.g. "ET", "CT", "MT", "PT").
 * Falls back to the DST-sensitive abbreviation from Intl for unrecognized timezones.
 */
const STABLE_TZ_ABBR: Record<string, string> = {
  'America/New_York': 'ET',
  'America/Chicago': 'CT',
  'America/Denver': 'MT',
  'America/Los_Angeles': 'PT',
  'America/Anchorage': 'AKT',
  'Pacific/Honolulu': 'HT',
};

const STABLE_TZ_NAME: Record<string, string> = {
  'America/New_York': 'Eastern time zone',
  'America/Chicago': 'Central time zone',
  'America/Denver': 'Mountain time zone',
  'America/Phoenix': 'Arizona Mountain time zone',
  'America/Los_Angeles': 'Pacific time zone',
  'America/Anchorage': 'Alaska time zone',
  'Pacific/Honolulu': 'Hawaii time zone',
};

/**
 * Returns a human-readable timezone name (e.g. "Pacific time zone").
 * Falls back to the IANA identifier for unrecognized timezones.
 */
export const getTimezoneName = (ianaTimezone: string): string => {
  return STABLE_TZ_NAME[ianaTimezone] ?? ianaTimezone;
};

/**
 * Returns a label describing how far the patient's timezone is from the
 * provider's, e.g. "3 hours behind" or "2.5 hours ahead".
 * Returns null if the timezones have the same UTC offset.
 */
export const getTimezoneOffsetLabel = (
  providerTz: string,
  patientTz: string,
): string | null => {
  const getOffsetMinutes = (tz: string): number => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date());
    const offsetStr = parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
    const match = offsetStr.match(/GMT([+-])(\d+)(?::(\d+))?/);
    if (!match) return 0;
    const sign = match[1] === '+' ? 1 : -1;
    const hours = parseInt(match[2], 10);
    const minutes = parseInt(match[3] ?? '0', 10);
    return sign * (hours * 60 + minutes);
  };

  const diffMinutes =
    getOffsetMinutes(patientTz) - getOffsetMinutes(providerTz);
  if (diffMinutes === 0) return null;

  const absDiff = Math.abs(diffMinutes);
  const hours = Math.floor(absDiff / 60);
  const mins = absDiff % 60;
  const label =
    mins === 0 ? `${hours} hour${hours !== 1 ? 's' : ''}` : `${hours}.5 hours`;
  return `${label} ${diffMinutes < 0 ? 'behind' : 'ahead'}`;
};

export const getStableTimezoneAbbr = (ianaTimezone: string): string => {
  if (STABLE_TZ_ABBR[ianaTimezone]) return STABLE_TZ_ABBR[ianaTimezone];

  try {
    return (
      new Intl.DateTimeFormat('en-US', {
        timeZone: ianaTimezone,
        timeZoneName: 'short',
      })
        .formatToParts(new Date())
        .find((p) => p.type === 'timeZoneName')?.value ?? ianaTimezone
    );
  } catch {
    return ianaTimezone;
  }
};

interface AvailabilityDateResult {
  formattedDate: string;
  isUrgent: boolean;
}

export const formatAvailabilityDate = (
  dateString: string | undefined | null,
): AvailabilityDateResult | null => {
  if (!dateString) {
    return null;
  }

  const date = parseISO(dateString);
  const today = startOfDay(new Date());

  if (date < today) {
    return null;
  }

  const daysRemaining = differenceInDays(date, today);

  return {
    formattedDate: format(date, 'MMMM d, yyyy'),
    isUrgent: daysRemaining <= 3,
  };
};
